import React from 'react';
import { Link } from 'react-router-dom';

const RiskManagement = () => {
  return (
    <div className="container py-5">
      <div className="row">
        <div className="col-lg-8 mx-auto">
          <h1 className="mb-4">🛡️ Risk Management</h1>
          
          <div className="card mb-4">
            <div className="card-body">
              <h3>Understanding Risk</h3>
              <p>
                All investments carry risk. Successful investors don't try to avoid risk entirely—they 
                identify, measure, and manage it effectively.
              </p>
              
              <h5 className="mt-4">Types of Investment Risk</h5>
              <div className="row">
                <div className="col-md-6">
                  <ul>
                    <li>
                      <strong>Market Risk:</strong> The possibility of investments declining due to market factors
                      <p className="small text-muted">
                        Also called systematic risk, this affects the entire market and can't be eliminated 
                        through diversification (e.g., recessions, interest rate changes).
                      </p>
                    </li>
                    <li>
                      <strong>Company Risk:</strong> Risk specific to an individual company
                      <p className="small text-muted">
                        Also called unsystematic risk, this can be reduced through diversification 
                        (e.g., poor management, product failures, competition).
                      </p>
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <ul>
                    <li>
                      <strong>Inflation Risk:</strong> The risk that investment returns won't keep pace with inflation
                      <p className="small text-muted">
                        Even "safe" investments can lose purchasing power if their returns don't exceed inflation.
                      </p>
                    </li>
                    <li>
                      <strong>Liquidity Risk:</strong> The risk of not being able to sell an investment quickly without a loss
                      <p className="small text-muted">
                        Some investments, like real estate or certain bonds, can be difficult to sell quickly 
                        at fair market value.
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  In March 2020, when COVID-19 was declared a global pandemic, the S&P 500 fell approximately 34% 
                  in just over a month. This was pure market risk that affected virtually all stocks. However, 
                  companies like Zoom and Amazon actually benefited from the pandemic (company-specific factors), 
                  with their stocks rising significantly while the broader market fell. This illustrates how 
                  different types of risk can affect investments in different ways during the same event.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Diversification</h3>
              <p>
                Don't put all your eggs in one basket. Spreading your investments across different assets 
                is one of the most effective risk management strategies.
              </p>
              
              <h5 className="mt-3">Effective Diversification Strategies</h5>
              <div className="row">
                <div className="col-md-6">
                  <h6>Asset Class Diversification</h6>
                  <p>
                    Spread investments across stocks, bonds, real estate, and other asset classes that 
                    respond differently to market conditions.
                  </p>
                </div>
                <div className="col-md-6">
                  <h6>Sector Diversification</h6>
                  <p>
                    Invest across different industries (technology, healthcare, consumer goods, etc.) 
                    to reduce exposure to sector-specific downturns.
                  </p>
                </div>
              </div>
              
              <div className="row mt-3">
                <div className="col-md-6">
                  <h6>Geographic Diversification</h6>
                  <p>
                    Invest in both domestic and international markets to reduce country-specific risk 
                    and capture global growth opportunities.
                  </p>
                </div>
                <div className="col-md-6">
                  <h6>Time Diversification</h6>
                  <p>
                    Use dollar-cost averaging (investing fixed amounts at regular intervals) to reduce 
                    the impact of market timing and volatility.
                  </p>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  During the 2008 financial crisis, a portfolio consisting solely of U.S. stocks (S&P 500) 
                  lost approximately 37%. However, a diversified portfolio with 40% U.S. stocks, 20% international 
                  stocks, 30% bonds, and 10% commodities would have lost only about 20%. Furthermore, this 
                  diversified portfolio recovered its value much faster in the subsequent years, demonstrating 
                  how proper diversification can both reduce losses and improve recovery times.
                </p>
              </div>
            </div>
          </div>
          
          <div className="card mb-4">
            <div className="card-body">
              <h3>Position Sizing</h3>
              <p>
                Position sizing refers to determining how much of your portfolio to allocate to each investment. 
                It's a critical aspect of risk management that many investors overlook.
              </p>
              
              <h5 className="mt-3">Position Sizing Methods</h5>
              <ul>
                <li>
                  <strong>Percentage of Portfolio:</strong> Allocate a fixed percentage of your portfolio to each position
                  <p className="small text-muted">
                    Example: No single stock position exceeds 5% of your total portfolio value.
                  </p>
                </li>
                <li>
                  <strong>Risk-Based Sizing:</strong> Size positions based on their risk level and your risk tolerance
                  <p className="small text-muted">
                    Example: Higher-risk investments get smaller allocations than lower-risk ones.
                  </p>
                </li>
                <li>
                  <strong>Kelly Criterion:</strong> A mathematical formula that determines optimal position size
                  <p className="small text-muted">
                    Used by professional traders to maximize long-term growth while minimizing risk of ruin.
                  </p>
                </li>
              </ul>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  Warren Buffett, despite his famous quote about concentration ("Diversification is protection 
                  against ignorance"), practices careful position sizing. In Berkshire Hathaway's portfolio, 
                  his largest positions (like Apple) represent significant allocations, but he built these 
                  positions gradually over time. When he first invested in Apple in 2016, it represented less 
                  than 1% of Berkshire's portfolio. As his conviction grew and the investment proved successful, 
                  he increased the position size. This demonstrates how even the world's most successful investor 
                  manages risk through careful position sizing.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Stop-Loss Strategies</h3>
              <p>
                Stop-loss orders are predetermined price levels at which you'll exit a position to limit losses. 
                They're essential tools for protecting capital and managing risk.
              </p>
              
              <div className="row mt-3">
                <div className="col-md-6">
                  <h5>Types of Stop-Loss Orders</h5>
                  <ul>
                    <li>
                      <strong>Fixed Percentage Stop:</strong> Exit when a position falls by a set percentage
                      <p className="small text-muted">
                        Example: Selling if a stock drops 7-10% below your purchase price
                      </p>
                    </li>
                    <li>
                      <strong>Technical Stop:</strong> Based on support levels, moving averages, or other technical indicators
                      <p className="small text-muted">
                        Example: Placing a stop just below a major support level or the 50-day moving average
                      </p>
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h5>Advanced Stop Techniques</h5>
                  <ul>
                    <li>
                      <strong>Trailing Stops:</strong> Stops that move up as the position gains value
                      <p className="small text-muted">
                        Allows you to lock in profits while still participating in upward trends
                      </p>
                    </li>
                    <li>
                      <strong>Volatility-Based Stops:</strong> Adjusts stop distance based on market volatility
                      <p className="small text-muted">
                        Wider stops for volatile assets, tighter stops for stable ones
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  In early 2020, an investor bought Amazon at $1,900 per share and set a 15% trailing stop. As Amazon 
                  rose to $3,400 during the pandemic, the trailing stop moved up to $2,890 (15% below the peak). When 
                  the market corrected in September 2020, Amazon fell to $2,870, triggering the stop and locking in a 
                  51% gain. Though Amazon later recovered, this example shows how trailing stops can protect substantial 
                  profits during market corrections while still allowing for significant upside participation.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Correlation and Portfolio Risk</h3>
              <p>
                Correlation measures how investments move in relation to each other. Understanding correlation 
                is crucial for building a truly diversified portfolio that can withstand various market conditions.
              </p>
              
              <h5 className="mt-3">Correlation Coefficients</h5>
              <div className="row">
                <div className="col-md-4">
                  <h6>Positive Correlation (+1.0)</h6>
                  <p>Assets move in the same direction</p>
                  <p className="small text-muted">
                    Example: Large-cap U.S. stocks often have high positive correlations with each other
                  </p>
                </div>
                <div className="col-md-4">
                  <h6>No Correlation (0)</h6>
                  <p>Assets move independently</p>
                  <p className="small text-muted">
                    Example: Gold often has low correlation with stocks, moving independently
                  </p>
                </div>
                <div className="col-md-4">
                  <h6>Negative Correlation (-1.0)</h6>
                  <p>Assets move in opposite directions</p>
                  <p className="small text-muted">
                    Example: Treasury bonds often have negative correlation with stocks during crises
                  </p>
                </div>
              </div>
              
              <h5 className="mt-3">Building a Low-Correlation Portfolio</h5>
              <p>
                Seek investments that serve different roles in your portfolio:
              </p>
              <ul>
                <li><strong>Growth Assets:</strong> Stocks, real estate, high-yield bonds</li>
                <li><strong>Defensive Assets:</strong> Treasury bonds, gold, cash</li>
                <li><strong>Alternative Assets:</strong> Commodities, managed futures, market-neutral strategies</li>
              </ul>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  During the March 2020 COVID-19 market crash, many traditionally uncorrelated assets suddenly moved together 
                  as investors rushed to raise cash, with even gold initially falling alongside stocks. However, U.S. Treasury 
                  bonds maintained their negative correlation, rising in value as stocks plummeted. A portfolio with a 30% 
                  allocation to long-term Treasury bonds would have significantly reduced overall losses during this period, 
                  demonstrating the importance of including truly uncorrelated assets in a portfolio.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Hedging Strategies</h3>
              <p>
                Hedging involves taking offsetting positions to reduce risk exposure. It's like buying insurance 
                for your portfolio against adverse market movements.
              </p>
              
              <div className="row mt-3">
                <div className="col-md-6">
                  <h5>Common Hedging Instruments</h5>
                  <ul>
                    <li>
                      <strong>Options:</strong> Put options can protect against downside in stock positions
                      <p className="small text-muted">
                        Buying put options gives you the right to sell at a predetermined price, limiting potential losses
                      </p>
                    </li>
                    <li>
                      <strong>Inverse ETFs:</strong> Move in the opposite direction of their underlying index
                      <p className="small text-muted">
                        Can offset losses in long positions during market declines
                      </p>
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h5>Hedging Techniques</h5>
                  <ul>
                    <li>
                      <strong>Portfolio Hedging:</strong> Protecting your entire portfolio against market risk
                      <p className="small text-muted">
                        Example: Buying S&P 500 put options to protect a diversified stock portfolio
                      </p>
                    </li>
                    <li>
                      <strong>Specific Hedging:</strong> Protecting against risks in individual positions
                      <p className="small text-muted">
                        Example: Currency hedging for international investments
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  In January 2020, a portfolio manager with $10 million in equity investments was concerned about market 
                  valuations and spent $200,000 (2% of portfolio value) on S&P 500 put options expiring in April. When 
                  the COVID-19 pandemic caused markets to crash in March, these options increased in value to $1.5 million, 
                  offsetting a significant portion of the losses in the equity portfolio. This hedge allowed the manager to 
                  maintain core positions through the downturn rather than selling at market lows, positioning the portfolio 
                  for the subsequent recovery.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Stress Testing Your Portfolio</h3>
              <p>
                Stress testing involves simulating how your portfolio would perform under various adverse scenarios. 
                This practice helps identify vulnerabilities before they're exposed in real market conditions.
              </p>
              
              <h5 className="mt-3">Common Stress Test Scenarios</h5>
              <div className="row">
                <div className="col-md-6">
                  <ul>
                    <li>
                      <strong>Historical Crises:</strong> How would your portfolio have performed during past market crashes?
                      <p className="small text-muted">
                        Examples: 2008 Financial Crisis, 2000 Dot-com Bubble, 1987 Black Monday
                      </p>
                    </li>
                    <li>
                      <strong>Interest Rate Shocks:</strong> Impact of sudden rate increases or decreases
                      <p className="small text-muted">
                        Particularly important for bond-heavy portfolios
                      </p>
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <ul>
                    <li>
                      <strong>Economic Scenarios:</strong> Effects of recession, inflation, or stagflation
                      <p className="small text-muted">
                        Different asset classes respond differently to economic conditions
                      </p>
                    </li>
                    <li>
                      <strong>Liquidity Crunch:</strong> What if you needed to sell 30% of your portfolio quickly?
                      <p className="small text-muted">
                        Tests both market liquidity and portfolio construction
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
              
              <h5 className="mt-3">Implementing Stress Tests</h5>
              <ul>
                <li>Use portfolio analysis tools that offer stress testing features</li>
                <li>Review historical performance during previous market disruptions</li>
                <li>Calculate potential drawdowns based on asset correlations</li>
                <li>Adjust your portfolio based on stress test results</li>
              </ul>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  In 2019, a financial advisor ran a stress test on client portfolios simulating a 30% market decline combined 
                  with a liquidity crisis similar to 2008. The test revealed that clients nearing retirement had insufficient 
                  cash reserves and too much exposure to high-yield corporate bonds, which could face liquidity issues in a crisis. 
                  Based on these results, the advisor increased cash positions and shifted some high-yield exposure to Treasury bonds. 
                  When the COVID-19 crisis hit in 2020, these adjusted portfolios experienced significantly less drawdown and had 
                  adequate liquidity, allowing clients to maintain their retirement plans despite market turbulence.
                </p>
              </div>
            </div>
          </div>
          
          <div className="card mb-4">
            <div className="card-body">
              <h3>Risk-Adjusted Returns</h3>
              <p>
                Risk-adjusted returns measure an investment's return relative to the amount of risk taken. These metrics 
                help investors compare investments with different risk profiles on a more equal basis.
              </p>
              
              <h5 className="mt-3">Key Risk-Adjusted Metrics</h5>
              <div className="row">
                <div className="col-md-6">
                  <ul>
                    <li>
                      <strong>Sharpe Ratio:</strong> Measures excess return per unit of total risk
                      <p className="small text-muted">
                        Formula: (Investment Return - Risk-Free Rate) ÷ Standard Deviation
                      </p>
                      <p className="small text-muted">
                        Higher is better; ratios above 1.0 are generally considered good
                      </p>
                    </li>
                    <li>
                      <strong>Sortino Ratio:</strong> Similar to Sharpe but only considers downside risk
                      <p className="small text-muted">
                        More relevant for investors primarily concerned with downside protection
                      </p>
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <ul>
                    <li>
                      <strong>Maximum Drawdown:</strong> Largest peak-to-trough decline
                      <p className="small text-muted">
                        Measures worst-case scenario and recovery potential
                      </p>
                    </li>
                    <li>
                      <strong>Calmar Ratio:</strong> Annual return divided by maximum drawdown
                      <p className="small text-muted">
                        Useful for evaluating investments with significant downside risk
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  Consider two mutual funds: Fund A returned 15% annually with 12% volatility, while Fund B returned 12% with 8% volatility. 
                  Assuming a risk-free rate of 3%, Fund A's Sharpe ratio is 1.0 ((15% - 3%) ÷ 12%), while Fund B's is 1.125 ((12% - 3%) ÷ 8%). 
                  Despite Fund A's higher absolute return, Fund B provided better risk-adjusted performance. An investor who leveraged Fund B 
                  to achieve the same 12% volatility as Fund A would have earned approximately 18% return, demonstrating how risk-adjusted 
                  metrics can reveal more efficient investments that might be overlooked when focusing solely on absolute returns.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Volatility Management</h3>
              <p>
                Volatility represents the degree of variation in an investment's price over time. Managing volatility is crucial 
                for maintaining emotional discipline and avoiding panic-driven decisions during market turbulence.
              </p>
              
              <h5 className="mt-3">Volatility Management Techniques</h5>
              <div className="row">
                <div className="col-md-6">
                  <h6>Portfolio Construction</h6>
                  <ul>
                    <li>
                      <strong>Volatility Targeting:</strong> Adjusting allocations to maintain a consistent portfolio volatility level
                    </li>
                    <li>
                      <strong>Low-Volatility Investing:</strong> Focusing on securities with historically lower price fluctuations
                    </li>
                    <li>
                      <strong>Volatility Harvesting:</strong> Rebalancing to capture benefits from volatility while maintaining allocations
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h6>Volatility Hedging</h6>
                  <ul>
                    <li>
                      <strong>VIX-Based Products:</strong> Using volatility ETFs or options to hedge against market turbulence
                    </li>
                    <li>
                      <strong>Options Strategies:</strong> Collars, protective puts, and covered calls to reduce portfolio volatility
                    </li>
                    <li>
                      <strong>Cash Management:</strong> Strategically increasing cash during high-volatility periods
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  In late 2019, a portfolio manager noticed that market volatility (VIX) was unusually low while valuations were stretched. 
                  They implemented a volatility management strategy by: (1) trimming positions in high-beta tech stocks, (2) adding low-volatility 
                  utility and consumer staples stocks, and (3) purchasing protective put options on 20% of the portfolio's value. When the 
                  COVID-19 pandemic hit in early 2020 and volatility spiked to record levels, this portfolio experienced about 40% less drawdown 
                  than the broader market. The reduced volatility allowed the manager to maintain their strategy and even deploy cash reserves 
                  near market lows, positioning the portfolio for strong gains during the subsequent recovery.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Psychological Aspects of Risk</h3>
              <p>
                Managing the psychological aspects of risk is often more challenging than managing the financial aspects. 
                Understanding behavioral biases and emotional responses to risk is essential for successful investing.
              </p>
              
              <h5 className="mt-3">Common Risk Psychology Pitfalls</h5>
              <div className="row">
                <div className="col-md-6">
                  <ul>
                    <li>
                      <strong>Loss Aversion:</strong> The tendency to feel losses more strongly than equivalent gains
                      <p className="small text-muted">
                        Can lead to holding losing positions too long and selling winners too early
                      </p>
                    </li>
                    <li>
                      <strong>Recency Bias:</strong> Overweighting recent events when assessing risk
                      <p className="small text-muted">
                        May cause excessive caution after downturns or complacency during bull markets
                      </p>
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <ul>
                    <li>
                      <strong>Overconfidence:</strong> Overestimating your ability to predict outcomes
                      <p className="small text-muted">
                        Often leads to taking excessive risks or inadequate diversification
                      </p>
                    </li>
                    <li>
                      <strong>Herd Mentality:</strong> Following the crowd rather than your investment plan
                      <p className="small text-muted">
                        Typically results in buying high and selling low
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
              
              <h5 className="mt-3">Psychological Risk Management</h5>
              <ul>
                <li>
                  <strong>Pre-commitment Strategies:</strong> Creating rules and plans before emotional situations arise
                </li>
                <li>
                  <strong>Risk Budgeting:</strong> Allocating a specific amount of risk capital for higher-risk investments
                </li>
                <li>
                  <strong>Scenario Planning:</strong> Mentally preparing for different market outcomes
                </li>
                <li>
                  <strong>Investment Journal:</strong> Documenting decisions and emotions to identify patterns
                </li>
              </ul>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  During the March 2020 market crash, an investor with a well-diversified portfolio saw their investments drop by 25%. 
                  Despite having a long-term investment plan, they experienced intense anxiety and considered selling everything to stop the losses. 
                  Before acting, they reviewed their investment journal where they had documented a similar feeling during the 2018 correction—after 
                  which markets quickly recovered. This self-awareness, combined with their pre-commitment to a rebalancing strategy at specific 
                  drawdown levels, helped them overcome the emotional urge to sell. Instead, they followed their plan to rebalance by adding to 
                  equity positions. By December 2020, their portfolio had not only recovered but reached new highs, demonstrating how psychological 
                  risk management can prevent costly emotional decisions.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Tail Risk Management</h3>
              <p>
                Tail risk refers to the risk of extreme, unexpected events that fall outside normal distribution patterns—the "fat tails" 
                of the probability curve. These rare but severe events can cause catastrophic losses if not properly managed.
              </p>
              
              <h5 className="mt-3">Tail Risk Protection Strategies</h5>
              <div className="row">
                <div className="col-md-6">
                  <h6>Portfolio-Based Approaches</h6>
                  <ul>
                    <li>
                      <strong>Barbell Strategy:</strong> Combining very safe assets with small allocations to high-risk, high-reward investments
                    </li>
                    <li>
                      <strong>Permanent Portfolio:</strong> Equal allocation to stocks, bonds, gold, and cash to withstand any economic environment
                    </li>
                    <li>
                      <strong>Antifragile Positioning:</strong> Including assets that potentially benefit from market stress
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h6>Derivative-Based Protection</h6>
                  <ul>
                    <li>
                      <strong>Out-of-the-Money Put Options:</strong> Relatively inexpensive insurance against severe market declines
                    </li>
                    <li>
                      <strong>Tail Risk Funds:</strong> Specialized investment vehicles designed to profit during market crashes
                    </li>
                    <li>
                      <strong>Volatility Calls:</strong> Options that profit from spikes in market volatility during crises
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  Universa Investments, a fund advised by "Black Swan" author Nassim Taleb, specializes in tail risk protection. 
                  In March 2020, when the S&P 500 fell approximately 30% due to the COVID-19 pandemic, Universa's tail risk strategy 
                  reportedly returned 3,600% in a single month. While this strategy typically loses money during normal market conditions 
                  (the cost of insurance), its massive returns during black swan events can more than compensate for the ongoing cost. 
                  Investors who allocated just 3-5% of their portfolio to such tail risk protection could have offset most or all of their 
                  losses during the crash, demonstrating how a small allocation to tail risk management can significantly improve long-term 
                  risk-adjusted returns.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Risk Management for Different Life Stages</h3>
              <p>
                Risk management should evolve throughout your life as your time horizon, financial goals, and capacity to bear losses change. 
                Effective risk management adapts to your current life stage while maintaining focus on long-term objectives.
              </p>
              
              <h5 className="mt-3">Risk Management by Life Stage</h5>
              <div className="row">
                <div className="col-md-4">
                  <h6>Early Career (20s-30s)</h6>
                  <ul>
                    <li>Longer time horizon allows for higher risk tolerance</li>
                    <li>Human capital (future earnings) is your largest asset</li>
                    <li>Focus on growth with higher equity allocations</li>
                    <li>Use dollar-cost averaging to manage market entry risk</li>
                    <li>Build emergency fund as first risk management tool</li>
                  </ul>
                </div>
                <div className="col-md-4">
                  <h6>Mid-Career (40s-50s)</h6>
                  <ul>
                    <li>Balance growth with increasing capital preservation</li>
                    <li>Diversify across more asset classes</li>
                    <li>Implement more sophisticated hedging strategies</li>
                    <li>Consider insurance products to protect peak earning years</li>
                    <li>Begin reducing exposure to single-company risk</li>
                  </ul>
                </div>
                <div className="col-md-4">
                  <h6>Pre/Post-Retirement (60s+)</h6>
                  <ul>
                    <li>Shift focus to capital preservation and income</li>
                    <li>Reduce volatility to manage sequence of returns risk</li>
                    <li>Maintain liquidity for near-term expenses</li>
                    <li>Consider longevity risk in portfolio construction</li>
                    <li>Implement withdrawal strategies to extend portfolio life</li>
                  </ul>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  Consider the case of two investors who experienced the 2008 financial crisis. A 30-year-old with a 90% stock allocation 
                  saw their portfolio drop by 40% but continued contributing to their retirement accounts, buying shares at depressed prices. 
                  By 2013, their portfolio had not only recovered but grown substantially due to both new contributions and market recovery. 
                  In contrast, a 65-year-old retiree with a 70% stock allocation who was withdrawing 4% annually experienced a "double whammy" 
                  of market losses and ongoing withdrawals, significantly depleting their portfolio. This retiree would have been better served 
                  by a more conservative allocation with dedicated income sources and cash reserves to avoid selling assets during market downturns. 
                  This example illustrates how the same risk management principles must be applied differently based on life stage and investment objectives.
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h3>Key Takeaways</h3>
              <div className="alert alert-info">
                <ul className="mb-0">
                  <li>Understanding different types of risk is essential for effective risk management</li>
                  <li>Diversification across multiple dimensions helps reduce portfolio volatility</li>
                  <li>Position sizing is crucial for limiting potential losses from any single investment</li>
                  <li>Stop-loss strategies help protect capital and lock in profits</li>
                  <li>Correlation analysis ensures your diversification is truly effective</li>
                  <li>Hedging strategies can provide insurance against specific market risks</li>
                  <li>Stress testing helps identify portfolio vulnerabilities before they're exposed</li>
                  <li>Risk-adjusted returns provide a more complete picture of investment performance</li>
                  <li>Volatility management techniques can help maintain emotional discipline</li>
                  <li>Understanding psychological aspects of risk is critical for successful investing</li>
                  <li>Tail risk protection strategies safeguard against extreme market events</li>
                  <li>Risk management approaches should evolve through different life stages</li>
                  <li>Risk management doesn't mean avoiding risk—it means taking calculated risks</li>
                  <li>The goal is to optimize returns relative to the amount of risk taken</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="text-center mt-4">
            <Link to="/market-analysis" className="btn btn-primary-custom">
              Next: Market Analysis →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskManagement;
